import { useTransactionHistoryContext } from "../../Contexts/TransactionHistoryContext";
import { KeyboardArrowLeft, Close as CloseIcon } from "@mui/icons-material";
import ErrorComponent from "../../Components/ErrorComponent";
import * as ScrollArea from "@radix-ui/react-scroll-area";
import { ScrollBar } from "../../Components/ScrollBar";
import LinkButton from "../../Components/LinkButton";
import { CircularProgress } from "@mui/material";
import Filters from "./Filters";
import dayjs from "dayjs";

export default function Transactions() {
  const {
    handleClickTransaction,
    handleBackToPolicies,
    transactionHistory,
    handleRemoveFilter,
    handleShowFilter,
    initTransactions,
    selectedPolicy,
    appliedFilter,
    filterCount,
    showFilters,
    loadState,
  } = useTransactionHistoryContext();

  const renderFilter = (item) => {
    if (item === "dateRange") {
      return (
        dayjs(appliedFilter[item].dateFrom).format("MM/DD/YYYY") +
        " - " +
        dayjs(appliedFilter[item].dateTo).format("MM/DD/YYYY")
      );
    } else if (["specialization", "medicalTestType"].includes(item)) {
      return appliedFilter[item] ? appliedFilter[item].value.toLowerCase() : "";
    }
    return appliedFilter[item]?.value || "";
  };

  const renderStatusContent = (item) => {
    let status = item.status;

    if (item.consultationType === "Procedures") {
      status = item.medicalTestStatus;
    }
    return (
      <div
        className={`rounded-full text-[14px] font-bold px-3 w-fit ml-2 ${renderStatusColor(
          status
        )}`}
      >
        {status}
      </div>
    );
  };

  const renderStatusColor = (status) => {
    if (
      status?.toLowerCase() === "pending" ||
      status?.toLowerCase() === "for approval"
    ) {
      return "text-[#0947ab] bg-[#c4d6f2]";
    } else if (
      status?.toLowerCase() === "completed" ||
      status?.toLowerCase() === "approved"
    ) {
      return "text-[#337951] bg-[#d7efe1]";
    } else if (status?.toLowerCase() === "cancelled") {
      return "text-[#c22020] bg-[#ffd1d1]";
    } else {
      return "text-[#000000] bg-[#dfdfdf]";
    }
  };

  let selectedFilters = Object.keys(appliedFilter).filter(
    (f) => appliedFilter[f].selected
  );

  let transList = null;
  if (transactionHistory.length > 0) {
    transList = (
      <div className="p-3">
        {transactionHistory.map((item) => (
          <button
            key={item.transactionId}
            className="w-full text-left border rounded-lg p-3 mb-3 transition ease-linear hover:scale-105"
            onClick={() => handleClickTransaction(item)}
          >
            <div className="flex items-start justify-between">
              <p className="font-bold line-clamp-2 flex-1">
                {item.transactionTitle}
              </p>
              {renderStatusContent(item)}
            </div>
            <p className="text-gray-500 font-semibold capitalize">
              {item.location?.split(":")[0]?.toLowerCase() || ""}
            </p>
            <p className="text-gray-400 text-sm">
              {dayjs(item.dateTime).format("MMMM DD, YYYY")} •{" "}
              {dayjs(item.dateTime).format("h:mm A")}
            </p>
          </button>
        ))}
      </div>
    );
  } else {
    transList = (
      <div className="text-center py-3 px-16">
        <p className="font-bold text-lg">No transaction history</p>
        <p className="text-gray-500">
          Currently, there are no transactions for the dates &nbsp;{" "}
          <span className="font-semibold text-black">
            {dayjs(appliedFilter.dateRange?.dateFrom).format("MMMM DD, YYYY")},
          </span>{" "}
          &nbsp; through &nbsp;
          <span className="font-semibold text-black">
            {dayjs(appliedFilter.dateRange?.dateTo).format("MMMM DD, YYYY")}.
          </span>{" "}
          &nbsp; Please adjust the date range filters to view the transactions
          you’re looking for.
        </p>
      </div>
    );
  }

  let content = null;
  if (loadState === "load") {
    content = (
      <div className="w-full flex justify-center items-center my-4">
        <CircularProgress />
      </div>
    );
  } else if (["ISE", "timeout", "retry"].includes(loadState)) {
    content = (
      <ErrorComponent
        onRetry={() => initTransactions("retry", selectedPolicy)}
        isLoading={loadState === "retry"}
      />
    );
  } else {
    content = transList;
  }

  return (
    <div className="flex flex-col items-center min-h-screen max-w-screen sm:bg-[#E7EEFA] ">
      <div className="bg-white w-screen mb-[56px] sm:my-24 sm:w-148 h-auto rounded-xl">
        <div className="flex p-4 border-b">
          <KeyboardArrowLeft
            className="cursor-pointer mr-3"
            onClick={handleBackToPolicies}
          />
          <p className="text-md font-bold flex-grow">Transaction history</p>
          <LinkButton
            disabled={["load", "ISE", "timeout", "retry"].includes(loadState)}
            className="text-sm text-blue-700 font-semibold mt-1 ml-2 hover:underline"
            onClick={handleShowFilter}
          >
            Filters {filterCount > 0 ? `(${filterCount})` : ""}
          </LinkButton>
        </div>
        <ScrollArea.Root className="w-full overflow-hidden">
          <ScrollArea.Viewport className="w-full min-h-[200px] h-[calc(100vh-113px)] sm:h-full sm:max-h-[600px] rounded">
            <ul className="flex sm:flex-wrap px-4 py-3 max-w-full overflow-auto">
              {selectedFilters.map((item) => (
                <li
                  className={`flex items-center border rounded pl-3 h-8
                    mr-2 sm:mb-2 ${item == "dateRange" && "pr-3"}`}
                  key={item}
                >
                  <label className="flex-1 text-sm whitespace-nowrap capitalize">
                    {renderFilter(item)}
                  </label>
                  {item !== "dateRange" && (
                    <button
                      className="flex items-center p-2 outline-none"
                      onClick={() => handleRemoveFilter(item)}
                    >
                      <CloseIcon sx={{ fontSize: 12 }} />
                    </button>
                  )}
                </li>
              ))}
            </ul>
            {content}
          </ScrollArea.Viewport>
          <ScrollBar />
        </ScrollArea.Root>
        {showFilters && <Filters />}
      </div>
    </div>
  );
}
